import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../api/httpClient";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { markEngagementPostsSeen } from "../../utils/engagementUnread";

const emptyForm = {
  title: "",
  body: "",
  isEnabled: true,
  isPinned: false,
  scheduledFor: "",
  creditedEmployeeId: "",
  links: [""],
  newFiles: [],
  removeAttachmentIds: [],
};

const POST_PAGE_SIZE = 8;
const COMMENT_PAGE_SIZE = 10;

const getOptimisticReactionPost = (post, reactionType) => {
  const previousReaction = post?.viewerReaction || null;
  const nextReaction = previousReaction === reactionType ? null : reactionType;
  const currentSummary = post?.reactionSummary || {};
  let likes = Number(currentSummary.likes || 0);
  let dislikes = Number(currentSummary.dislikes || 0);

  if (previousReaction === "Like") {
    likes = Math.max(0, likes - 1);
  }
  if (previousReaction === "Dislike") {
    dislikes = Math.max(0, dislikes - 1);
  }
  if (nextReaction === "Like") {
    likes += 1;
  }
  if (nextReaction === "Dislike") {
    dislikes += 1;
  }

  return {
    ...post,
    viewerReaction: nextReaction,
    reactionSummary: {
      ...currentSummary,
      likes,
      dislikes,
    },
  };
};

const reconcileReactionPost = (post, reactionType, responseData) => ({
  ...post,
  viewerReaction: responseData?.action === "removed" ? null : reactionType,
  reactionSummary: {
    ...(post?.reactionSummary || {}),
    ...(responseData?.summary || {}),
  },
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const toDateTimeLocalInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const adjusted = new Date(
    parsed.getTime() - parsed.getTimezoneOffset() * 60000,
  );
  return adjusted.toISOString().slice(0, 16);
};

const mentionMatch = (text) => {
  const value = `${text || ""}`;
  const match = value.match(/(^|\s)@([^\s@]*)$/);
  if (!match) return null;
  return {
    query: (match[2] || "").trim().toLowerCase(),
    start: match.index + match[1].length,
    end: value.length,
  };
};

const renderMentions = (text) => {
  const chunks = `${text || ""}`.split(/(@\[[^\]]+\])/g);
  return chunks.map((chunk, index) => {
    if (/^@\[[^\]]+\]$/.test(chunk)) {
      return (
        <span
          key={`mention-${index}`}
          className="font-medium text-[#1234CD]"
        >
          {chunk}
        </span>
      );
    }
    return <span key={`text-${index}`}>{chunk}</span>;
  });
};

const wasCommentEdited = (comment) => {
  const createdAt = new Date(comment?.createdAt || 0).getTime();
  const updatedAt = new Date(comment?.updatedAt || 0).getTime();
  return Number.isFinite(createdAt) && Number.isFinite(updatedAt) && updatedAt > createdAt + 1000;
};

export default function Engagement() {
  const apiBaseUrl = getApiBaseUrl();
  const {
    companyAccount,
    currentEmployee,
    loading: employeeLoading,
  } = useCurrentEmployee();
  const actingEmployeeId = currentEmployee?.id || "";
  const actingAs = companyAccount ? "admin" : "employee";
  const engagementCacheKey = useMemo(
    () =>
      `cehr_engagement_posts_${actingAs}_${actingAs === "employee" ? actingEmployeeId || "none" : "admin"}`,
    [actingAs, actingEmployeeId],
  );
  const cachedPostsData = useMemo(
    () =>
      readSessionCache(engagementCacheKey, {
        posts: [],
        postsPage: 1,
        hasMorePosts: false,
      }),
    [engagementCacheKey],
  );
  const [posts, setPosts] = useState(cachedPostsData?.posts || []);
  const [employees, setEmployees] = useState([]);
  const [canCreatePost, setCanCreatePost] = useState(
    companyAccount || currentEmployee?.modulePermissions?.includes("engagement") || !!currentEmployee?.canCreateEngagementPosts,
  );
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [updatingAccessId, setUpdatingAccessId] = useState("");
  const [loading, setLoading] = useState(!cachedPostsData?.posts?.length);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [postsPage, setPostsPage] = useState(
    Number(cachedPostsData?.postsPage || 1),
  );
  const [hasMorePosts, setHasMorePosts] = useState(
    Boolean(cachedPostsData?.hasMorePosts),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formState, setFormState] = useState(emptyForm);
  const [reactionModal, setReactionModal] = useState({
    open: false,
    type: "Like",
    people: [],
    loading: false,
    error: "",
  });
  const [deletePostModal, setDeletePostModal] = useState({
    open: false,
    post: null,
    loading: false,
    error: "",
  });
  const pendingReactionPostIds = useRef(new Set());
  const [visibilityPostId, setVisibilityPostId] = useState("");
  const [commentModal, setCommentModal] = useState({
    open: false,
    post: null,
    text: "",
    loading: false,
    loadingMore: false,
    error: "",
    page: 1,
    hasMore: false,
    mention: null,
    editingId: "",
    editText: "",
    savingEdit: false,
    replyingTo: null,
  });
  const [deleteCommentModal, setDeleteCommentModal] = useState({
    open: false,
    postId: "",
    comment: null,
    loading: false,
    error: "",
  });
  const sentinelRef = useRef(null);
  const commentComposerRef = useRef(null);
  const replyToComment = (comment) => {
    setCommentModal((prev) => ({ ...prev, replyingTo: comment, text: "", mention: null, error: "" }));
    requestAnimationFrame(() => {
      commentComposerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      commentComposerRef.current?.focus();
    });
  };

  const actorEmployeeId = actingAs === "employee" ? actingEmployeeId : "";

  const loadPosts = async ({
    page = 1,
    append = false,
    silent = false,
  } = {}) => {
    if (!apiBaseUrl) {
      setError("API base URL is not configured.");
      return;
    }

    try {
      if (append) {
        setLoadingMorePosts(true);
      } else if (!silent && posts.length === 0) {
        setLoading(true);
      }
      setError("");

      const response = await axios.get(
        `${apiBaseUrl}/organizations/engagement/posts`,
        {
          params: {
            viewer: actingAs,
            employeeId: actorEmployeeId || undefined,
            page,
            limit: POST_PAGE_SIZE,
          },
        },
      );

      const nextPosts = response?.data?.posts || [];
      setPosts((prev) => (append ? [...prev, ...nextPosts] : nextPosts));
      const pagination = response?.data?.pagination || {};
      setPostsPage(Number(pagination?.page || page));
      setHasMorePosts(Boolean(pagination?.hasMore));
      writeSessionCache(engagementCacheKey, {
        posts: append ? [...posts, ...nextPosts] : nextPosts,
        postsPage: Number(pagination?.page || page),
        hasMorePosts: Boolean(pagination?.hasMore),
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        "Unable to load engagement posts.";
      setError(Array.isArray(message) ? message.join(", ") : message);
      if (!append && posts.length === 0) {
        setPosts([]);
      }
    } finally {
      if (append) {
        setLoadingMorePosts(false);
      } else {
        setLoading(false);
      }
    }
  };

  const loadEmployees = async () => {
    if (!apiBaseUrl) {
      return;
    }

    try {
      const response = await axios.get(
        `${apiBaseUrl}/organizations/employees`,
        {
          params: { status: "approved" },
        },
      );
      const nextEmployees = response?.data?.employees || [];
      setEmployees(nextEmployees);
      if (!companyAccount) {
        const me = nextEmployees.find((employee) => employee.id === actingEmployeeId);
        setCanCreatePost(!!me?.canCreateEngagementPosts);
      }
    } catch {
      setEmployees([]);
    }
  };

  const updatePostingAccess = async (employee) => {
    if (!apiBaseUrl || !employee?.id) return;

    try {
      setUpdatingAccessId(employee.id);
      setError("");
      const nextValue = !employee.canCreateEngagementPosts;
      await axios.patch(
        `${apiBaseUrl}/organizations/engagement/post-access/${encodeURIComponent(employee.id)}`,
        { canCreateEngagementPosts: nextValue },
      );
      setEmployees((previous) =>
        previous.map((item) =>
          item.id === employee.id
            ? { ...item, canCreateEngagementPosts: nextValue }
            : item,
        ),
      );
      setSuccess(
        `${employee.name || "Employee"} ${nextValue ? "can now" : "can no longer"} create engagement posts.`,
      );
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to update posting access.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setUpdatingAccessId("");
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [apiBaseUrl, actingEmployeeId, companyAccount]);

  useEffect(() => {
    const cached = readSessionCache(engagementCacheKey, null);
    if (cached) {
      setPosts(cached.posts || []);
      setPostsPage(Number(cached.postsPage || 1));
      setHasMorePosts(Boolean(cached.hasMorePosts));
      setLoading(!cached?.posts?.length);
    }
  }, [engagementCacheKey]);

  useEffect(() => {
    if (!companyAccount && employeeLoading) {
      return;
    }

    loadPosts({ page: 1, append: false, silent: true });
  }, [apiBaseUrl, actorEmployeeId, companyAccount, employeeLoading]);

  useEffect(() => {
    if (!companyAccount && actorEmployeeId && posts.length > 0) {
      markEngagementPostsSeen(posts, actorEmployeeId);
    }
  }, [actorEmployeeId, companyAccount, posts]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPosts({ page: 1, append: false, silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [apiBaseUrl, actorEmployeeId]);

  useEffect(() => {
    if (!sentinelRef.current || loading || loadingMorePosts || !hasMorePosts) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadPosts({ page: postsPage + 1, append: true });
        }
      },
      {
        rootMargin: "220px",
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [
    postsPage,
    hasMorePosts,
    loading,
    loadingMorePosts,
    apiBaseUrl,
    actorEmployeeId,
  ]);

  const openCreate = () => {
    setEditingPost(null);
    setFormState(emptyForm);
    setShowCreateModal(true);
    setError("");
    setSuccess("");
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setFormState({
      title: post.title || "",
      body: post.body || "",
      isEnabled: !!post.isEnabled,
      isPinned: !!post.isPinned,
      scheduledFor: toDateTimeLocalInput(post?.scheduledFor),
      creditedEmployeeId: post?.creditedEmployee?.id || "",
      links: (post.attachments || [])
        .filter((item) => item.type === "Link")
        .map((item) => item.url),
      newFiles: [],
      removeAttachmentIds: [],
    });
    setShowCreateModal(true);
    setError("");
    setSuccess("");
  };

  const updateLink = (index, value) => {
    setFormState((prev) => ({
      ...prev,
      links: prev.links.map((link, idx) => (idx === index ? value : link)),
    }));
  };

  const addLinkField = () => {
    setFormState((prev) => ({ ...prev, links: [...prev.links, ""] }));
  };

  const removeLinkField = (index) => {
    setFormState((prev) => ({
      ...prev,
      links: prev.links.filter((_, idx) => idx !== index),
    }));
  };

  const submitPost = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setError("API base URL is not configured.");
      return;
    }

    const links = (formState.links || [])
      .map((item) => `${item || ""}`.trim())
      .filter(Boolean);
    if (!formState.title.trim() || !formState.body.trim()) {
      setError("Post title and body are required.");
      return;
    }

    const payload = new FormData();
    payload.append("title", formState.title.trim());
    payload.append("body", formState.body.trim());
    payload.append("isEnabled", String(!!formState.isEnabled));
    payload.append("isPinned", String(!!formState.isPinned));

    const scheduledFor = `${formState.scheduledFor || ""}`.trim();
    if (scheduledFor) {
      payload.append("scheduledFor", new Date(scheduledFor).toISOString());
    } else if (editingPost) {
      payload.append("scheduledFor", "");
    }

    if (formState.creditedEmployeeId) {
      payload.append("creditedEmployeeId", formState.creditedEmployeeId);
    }

    if (links.length > 0) {
      payload.append("linksJson", JSON.stringify(links));
    }

    if (editingPost && formState.removeAttachmentIds.length > 0) {
      payload.append(
        "removeAttachmentIdsJson",
        JSON.stringify(formState.removeAttachmentIds),
      );
    }

    (formState.newFiles || []).forEach((file) => {
      payload.append("files", file);
    });

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      if (editingPost?.id) {
        await axios.patch(
          `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(editingPost.id)}`,
          payload,
        );
        setSuccess("Post updated successfully.");
      } else {
        await axios.post(
          `${apiBaseUrl}/organizations/engagement/posts`,
          payload,
        );
        setSuccess("Post created successfully.");
      }

      setShowCreateModal(false);
      setEditingPost(null);
      setFormState(emptyForm);
      await loadPosts({ page: 1, append: false });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to save post.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeletePostModal = (post) => {
    setDeletePostModal({ open: true, post, loading: false, error: "" });
  };

  const closeDeletePostModal = () => {
    setDeletePostModal({ open: false, post: null, loading: false, error: "" });
  };

  const confirmDeletePost = async () => {
    if (!apiBaseUrl || !deletePostModal.post?.id) return;

    try {
      setDeletePostModal((prev) => ({ ...prev, loading: true, error: "" }));
      await axios.delete(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(deletePostModal.post.id)}`,
      );
      setSuccess("Post deleted successfully.");
      closeDeletePostModal();
      await loadPosts({ page: 1, append: false });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to delete post.";
      setDeletePostModal((prev) => ({
        ...prev,
        loading: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
    }
  };

  const toggleVisibility = async (post) => {
    if (!apiBaseUrl || !post?.id) return;

    try {
      setVisibilityPostId(post.id);
      setError("");
      await axios.patch(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(post.id)}/visibility`,
        {
          isEnabled: !post.isEnabled,
        },
      );
      await loadPosts({ page: 1, append: false });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        "Unable to update post visibility.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setVisibilityPostId("");
    }
  };

  const reactToPost = async (postId, reactionType) => {
    if (!apiBaseUrl || pendingReactionPostIds.current.has(postId)) return;

    pendingReactionPostIds.current.add(postId);

    const previousPosts = posts;
    const optimisticPosts = previousPosts.map((post) =>
      post.id === postId ? getOptimisticReactionPost(post, reactionType) : post,
    );

    setPosts(optimisticPosts);
    writeSessionCache(engagementCacheKey, {
      posts: optimisticPosts,
      postsPage,
      hasMorePosts,
    });

    try {
      setError("");
      const response = await axios.post(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(postId)}/reactions`,
        {
          reactionType,
          employeeId: actorEmployeeId || undefined,
        },
      );
      const reconciledPosts = optimisticPosts.map((post) =>
        post.id === postId ? reconcileReactionPost(post, reactionType, response?.data || {}) : post,
      );
      setPosts(reconciledPosts);
      writeSessionCache(engagementCacheKey, {
        posts: reconciledPosts,
        postsPage,
        hasMorePosts,
      });
    } catch (requestError) {
      setPosts(previousPosts);
      writeSessionCache(engagementCacheKey, {
        posts: previousPosts,
        postsPage,
        hasMorePosts,
      });
      const message =
        requestError?.response?.data?.message || "Unable to update reaction.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      pendingReactionPostIds.current.delete(postId);
    }
  };

  const openReactionPeople = async (postId, type) => {
    if (!apiBaseUrl) return;

    try {
      setReactionModal({
        open: true,
        type,
        people: [],
        loading: true,
        error: "",
      });
      const response = await axios.get(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(postId)}/reactions`,
        {
          params: { type },
        },
      );
      setReactionModal({
        open: true,
        type,
        people: response?.data?.people || [],
        loading: false,
        error: "",
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to load reactions.";
      setReactionModal({
        open: true,
        type,
        people: [],
        loading: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      });
    }
  };

  const loadSinglePost = async (postId, options = {}) => {
    if (!apiBaseUrl || !postId) {
      return null;
    }

    try {
      const response = await axios.get(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(postId)}`,
        {
          params: {
            viewer: actingAs,
            employeeId: actorEmployeeId || undefined,
            commentPage: options.commentPage || 1,
            commentLimit: options.commentLimit || COMMENT_PAGE_SIZE,
          },
        },
      );
      return {
        post: response?.data?.post || null,
        commentPagination: response?.data?.commentPagination || null,
      };
    } catch {
      return null;
    }
  };

  const openComments = async (post) => {
    setCommentModal({
      open: true,
      post,
      text: "",
      loading: true,
      loadingMore: false,
      error: "",
      page: 1,
      hasMore: false,
      mention: null,
      editingId: "",
      editText: "",
      savingEdit: false,
    });
    const refreshedPost = await loadSinglePost(post.id, {
      commentPage: 1,
      commentLimit: COMMENT_PAGE_SIZE,
    });
    if (refreshedPost?.post) {
      setCommentModal({
        open: true,
        post: refreshedPost.post,
        text: "",
        loading: false,
        loadingMore: false,
        error: "",
        page: 1,
        hasMore: !!refreshedPost?.commentPagination?.hasMore,
        mention: null,
        editingId: "",
        editText: "",
        savingEdit: false,
      });
      return;
    }
    setCommentModal((prev) => ({ ...prev, loading: false }));
  };

  const submitComment = async () => {
    if (!apiBaseUrl || !commentModal?.post?.id) return;

    const text = `${commentModal.text || ""}`.trim();
    if (!text) {
      setCommentModal((prev) => ({
        ...prev,
        error: "Comment body is required.",
      }));
      return;
    }

    try {
      setCommentModal((prev) => ({ ...prev, loading: true, error: "" }));
      await axios.post(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(commentModal.post.id)}/comments`,
        {
          body: text,
          employeeId: actorEmployeeId || undefined,
          parentCommentId: commentModal.replyingTo?.id || undefined,
        },
      );
      await loadPosts({ page: 1, append: false });
      const refreshedPost = await loadSinglePost(commentModal.post.id, {
        commentPage: 1,
        commentLimit: COMMENT_PAGE_SIZE,
      });
      setCommentModal({
        open: true,
        post: refreshedPost?.post || commentModal.post,
        text: "",
        loading: false,
        loadingMore: false,
        error: "",
        page: 1,
        hasMore: !!refreshedPost?.commentPagination?.hasMore,
        mention: null,
        editingId: "",
        editText: "",
        savingEdit: false,
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to add comment.";
      setCommentModal((prev) => ({
        ...prev,
        loading: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
    }
  };

  const loadMoreComments = async () => {
    if (
      !commentModal?.post?.id ||
      commentModal.loadingMore ||
      !commentModal.hasMore
    ) {
      return;
    }

    try {
      setCommentModal((prev) => ({ ...prev, loadingMore: true, error: "" }));
      const nextPage = Number(commentModal.page || 1) + 1;
      const response = await loadSinglePost(commentModal.post.id, {
        commentPage: nextPage,
        commentLimit: COMMENT_PAGE_SIZE,
      });
      if (!response?.post) {
        setCommentModal((prev) => ({ ...prev, loadingMore: false }));
        return;
      }

      setCommentModal((prev) => ({
        ...prev,
        loadingMore: false,
        page: nextPage,
        hasMore: !!response?.commentPagination?.hasMore,
        post: {
          ...(prev.post || {}),
          ...response.post,
          comments: [
            ...(prev.post?.comments || []),
            ...(response.post?.comments || []),
          ],
        },
      }));
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        "Unable to load more comments.";
      setCommentModal((prev) => ({
        ...prev,
        loadingMore: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
    }
  };

  const deleteComment = async (postId, commentId) => {
    if (!apiBaseUrl) return;

    try {
      setDeleteCommentModal((prev) => ({ ...prev, loading: true, error: "" }));
      await axios.delete(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
      );
      await loadPosts({ page: 1, append: false });
      const refreshedPost = await loadSinglePost(postId, {
        commentPage: 1,
        commentLimit: COMMENT_PAGE_SIZE,
      });
      if (refreshedPost?.post) {
        setCommentModal((prev) => ({
          ...prev,
          post: refreshedPost.post,
          page: 1,
          hasMore: !!refreshedPost?.commentPagination?.hasMore,
        }));
      }
      setDeleteCommentModal({ open: false, postId: "", comment: null, loading: false, error: "" });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message || "Unable to delete comment.";
      setCommentModal((prev) => ({
        ...prev,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
    }
  };

  const saveCommentEdit = async (postId, commentId) => {
    const body = `${commentModal.editText || ""}`.trim();
    if (!apiBaseUrl || !body) {
      setCommentModal((prev) => ({ ...prev, error: "Comment body is required." }));
      return;
    }

    try {
      setCommentModal((prev) => ({ ...prev, savingEdit: true, error: "" }));
      await axios.patch(
        `${apiBaseUrl}/organizations/engagement/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
        { body },
      );
      const refreshedPost = await loadSinglePost(postId, {
        commentPage: 1,
        commentLimit: COMMENT_PAGE_SIZE,
      });
      setCommentModal((prev) => ({
        ...prev,
        post: refreshedPost?.post || prev.post,
        page: 1,
        hasMore: !!refreshedPost?.commentPagination?.hasMore,
        editingId: "",
        editText: "",
        savingEdit: false,
      }));
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to update comment.";
      setCommentModal((prev) => ({
        ...prev,
        savingEdit: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
      setDeleteCommentModal((prev) => ({
        ...prev,
        loading: false,
        error: Array.isArray(message) ? message.join(", ") : message,
      }));
    }
  };

  const selectedPostForEditAttachments = useMemo(() => {
    if (!editingPost) return [];
    return (editingPost.attachments || []).filter(
      (item) => item.type !== "Link",
    );
  }, [editingPost]);

  const mentionCandidates = useMemo(() => {
    const mention = commentModal.mention;
    if (!mention) return [];
    const query = mention.query || "";
    return (employees || [])
      .filter((employee) => {
        const text =
          `${employee.name || ""} ${employee.emailAddress || ""}`.toLowerCase();
        return query ? text.includes(query) : true;
      })
      .slice(0, 6);
  }, [commentModal.mention, employees]);

  const updateCommentText = (value) => {
    const mention = mentionMatch(value);
    setCommentModal((prev) => ({ ...prev, text: value, mention }));
  };

  const insertMention = (employee) => {
    setCommentModal((prev) => {
      const current = `${prev.text || ""}`;
      const activeMention = mentionMatch(current);
      if (!activeMention) {
        return prev;
      }

      const token = `@[${employee.name || "employee"}]`;
      const updatedText = `${current.slice(0, activeMention.start)}${token} ${current.slice(activeMention.end)}`;
      return {
        ...prev,
        text: updatedText,
        mention: null,
      };
    });
  };

  return (
    <div className="font-Nunito p-6">
      <div className="my-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-semibold text-[#071C50]">Engagement</p>
          <p className="text-sm text-[#5F6B8A]">
            {companyAccount
              ? "Create and manage announcements, reactions, and comments."
              : canCreatePost
                ? "Create posts, react, comment, and stay up to date with company announcements."
              : "React, comment, and stay up to date with company announcements."}
          </p>
        </div>

        {companyAccount || canCreatePost ? (
          <div className="flex flex-wrap items-center gap-2">
            {companyAccount ? (
              <button
                type="button"
                onClick={() => setShowAccessModal(true)}
                className="rounded-xl border border-[#1234CD] px-5 py-2 text-sm font-semibold text-[#1234CD]"
              >
                Manage posting access
              </button>
            ) : null}
            <button
              type="button"
              onClick={openCreate}
              className="rounded-xl bg-[#1234CD] px-5 py-2 text-sm font-semibold text-white"
            >
              Create post
            </button>
          </div>
        ) : null}
      </div>
      {success ? (
        <p className="mb-3 rounded-lg bg-[#ECFDF3] px-3 py-2 text-sm text-[#027A48]">
          {success}
        </p>
      ) : null}
      {error ? (
        <p className="mb-3 rounded-lg bg-[#FFF1F3] px-3 py-2 text-sm text-[#B42318]">
          {error}
        </p>
      ) : null}

      {loading && posts.length === 0 ? (
        <FetchLoadingState
          message="Loading posts..."
          className="min-h-[220px]"
        />
      ) : null}

      {!loading && posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#CDD6F9] bg-white px-6 py-12 text-center text-[#5F6B8A]">
          No engagement posts yet.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {posts.map((post) => {
          const imageAttachment = (post.attachments || []).find(
            (item) => item.type === "Image",
          );
          const videoAttachment = (post.attachments || []).find(
            (item) => item.type === "Video",
          );
          const linkAttachments = (post.attachments || []).filter(
            (item) => item.type === "Link",
          );

          return (
            <article
              key={post.id}
              className="flex h-full flex-col rounded-2xl border border-[#E5EAF9] bg-white p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-[#071C50]">
                    {post.title}
                  </h3>
                  <p className="mt-1 text-xs text-[#667085]">
                    Posted {formatDateTime(post.createdAt)}
                  </p>
                  {post.scheduledFor ? (
                    <p className="mt-1 text-xs text-[#B54708]">
                      Scheduled for {formatDateTime(post.scheduledFor)}
                    </p>
                  ) : null}
                  {post.creditedEmployee ? (
                    <p className="mt-2 text-sm text-[#1234CD]">
                      Credit: {post.creditedEmployee.name} (
                      {post.creditedEmployee.email || "-"})
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${post.isEnabled ? "bg-[#ECFDF3] text-[#027A48]" : "bg-[#FFF1F3] text-[#B42318]"}`}
                  >
                    {post.isEnabled
                      ? "Visible to employees"
                      : "Hidden from employees"}
                  </span>
                  {post.isPinned ? (
                    <span className="rounded-full bg-[#EFF8FF] px-3 py-1 text-xs font-semibold text-[#175CD3]">
                      Pinned
                    </span>
                  ) : null}
                  {post.isScheduled ? (
                    <span className="rounded-full bg-[#FEF7E0] px-3 py-1 text-xs font-semibold text-[#B54708]">
                      Scheduled
                    </span>
                  ) : null}

                  {companyAccount ? (
                    <>
                      <button
                        type="button"
                        disabled={visibilityPostId === post.id}
                        onClick={() => toggleVisibility(post)}
                        className="rounded-lg border border-[#D0D7F2] px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {visibilityPostId === post.id ? "Saving..." : post.isEnabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(post)}
                        className="rounded-lg border border-[#D0D7F2] px-3 py-1 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeletePostModal(post)}
                        className="rounded-lg border border-[#F7C5CC] px-3 py-1 text-xs text-[#B42318]"
                      >
                        Delete
                      </button>
                    </>
                  ) : null}
                  <Link
                    to={`/engagement/${encodeURIComponent(post.id)}`}
                    className="rounded-lg border border-[#D0D7F2] px-3 py-1 text-xs text-[#1234CD]"
                  >
                    View
                  </Link>
                </div>
              </div>

              <p className="mt-3 whitespace-pre-wrap text-sm text-[#344054]">
                {post.body}
              </p>

              {imageAttachment ? (
                <div className="mt-4">
                  <img
                    src={buildAssetUrl(imageAttachment.url)}
                    alt={imageAttachment.label || "post media"}
                    className="h-56 w-full rounded-xl object-cover"
                  />
                </div>
              ) : null}

              {videoAttachment ? (
                <div className="mt-4 space-y-3">
                  <video
                    controls
                    className="max-h-[420px] w-full rounded-xl bg-black"
                    src={buildAssetUrl(videoAttachment.url)}
                  />
                </div>
              ) : null}

              {linkAttachments.length > 0 ? (
                <div className="mt-4 space-y-2 rounded-xl bg-[#F8FAFF] p-3">
                  {linkAttachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block break-all text-sm text-[#1234CD] underline"
                    >
                      {attachment.url}
                    </a>
                  ))}
                </div>
              ) : null}

              <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-[#EEF1FB] pt-4 text-sm">
                <div className="inline-flex overflow-hidden rounded-lg border border-[#D0D7F2]">
                  <button
                    type="button"
                    disabled={pendingReactionPostIds.current.has(post.id)}
                    onClick={() => reactToPost(post.id, "Like")}
                    className={`px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50 ${post.viewerReaction === "Like" ? "bg-[#EEF2FF] text-[#1234CD]" : "bg-white"}`}
                  >
                    Like
                  </button>
                  <button
                    type="button"
                    onClick={() => openReactionPeople(post.id, "Like")}
                    className="border-l border-[#D0D7F2] bg-white px-2.5 py-1 font-semibold text-[#1234CD] hover:bg-[#F8FAFF]"
                    aria-label={`View ${post.reactionSummary?.likes || 0} likes`}
                  >
                    {post.reactionSummary?.likes || 0}
                  </button>
                </div>
                <div className="inline-flex overflow-hidden rounded-lg border border-[#D0D7F2]">
                  <button
                    type="button"
                    disabled={pendingReactionPostIds.current.has(post.id)}
                    onClick={() => reactToPost(post.id, "Dislike")}
                    className={`px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50 ${post.viewerReaction === "Dislike" ? "bg-[#FFF1F3] text-[#B42318]" : "bg-white"}`}
                  >
                    Dislike
                  </button>
                  <button
                    type="button"
                    onClick={() => openReactionPeople(post.id, "Dislike")}
                    className="border-l border-[#D0D7F2] bg-white px-2.5 py-1 font-semibold text-[#B42318] hover:bg-[#FFF8F8]"
                    aria-label={`View ${post.reactionSummary?.dislikes || 0} dislikes`}
                  >
                    {post.reactionSummary?.dislikes || 0}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => openComments(post)}
                  className="rounded-lg border border-[#D0D7F2] px-3 py-1"
                >
                  Comments ({post.reactionSummary?.comments || 0})
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div
        ref={sentinelRef}
        className="h-2"
      />
      {loadingMorePosts ? (
        <p className="mt-3 text-sm text-gray-500">Loading more posts...</p>
      ) : null}
      {!loading && !loadingMorePosts && posts.length > 0 && !hasMorePosts ? (
        <p className="mt-3 text-center text-xs text-[#98A2B3]">
          You have reached the end of the feed.
        </p>
      ) : null}

      {showCreateModal && (companyAccount || canCreatePost) ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={submitPost}
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#071C50]">
                {editingPost ? "Edit Post" : "Create Post"}
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-2xl text-[#667085]"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Post Title
                </label>
                <input
                  value={formState.title}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Post Body
                </label>
                <textarea
                  value={formState.body}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      body: event.target.value,
                    }))
                  }
                  rows={6}
                  className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                />
              </div>

              {companyAccount ? <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Credit Employee (optional)
                  </label>
                  <select
                    value={formState.creditedEmployeeId}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        creditedEmployeeId: event.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                  >
                    <option value="">No credit</option>
                    {employees.map((employee) => (
                      <option
                        key={employee.id}
                        value={employee.id}
                      >
                        {employee.name} ({employee.emailAddress || "-"})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Visibility
                  </label>
                  <select
                    value={formState.isEnabled ? "true" : "false"}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        isEnabled: event.target.value === "true",
                      }))
                    }
                    className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                  >
                    <option value="true">Visible to employees</option>
                    <option value="false">Hidden from employees</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Pin post
                  </label>
                  <select
                    value={formState.isPinned ? "true" : "false"}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        isPinned: event.target.value === "true",
                      }))
                    }
                    className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                  >
                    <option value="false">Normal order</option>
                    <option value="true">Pin to top</option>
                  </select>
                </div>
              </div> : null}

              {companyAccount ? <div>
                <label className="mb-1 block text-sm font-medium">
                  Schedule publish (optional)
                </label>
                <input
                  type="datetime-local"
                  value={formState.scheduledFor}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      scheduledFor: event.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                />
                <p className="mt-1 text-xs text-[#667085]">
                  Leave empty to publish immediately.
                </p>
              </div> : null}

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Add image/video attachments (multiple)
                </label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      newFiles: Array.from(event.target.files || []),
                    }))
                  }
                  className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2"
                />
                {formState.newFiles.length > 0 ? (
                  <p className="mt-1 text-xs text-[#667085]">
                    {formState.newFiles.length} file(s) selected.
                  </p>
                ) : null}
              </div>

              {editingPost && selectedPostForEditAttachments.length > 0 ? (
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Existing attachments
                  </label>
                  <div className="space-y-2 rounded-lg border border-[#E4E7EC] p-3">
                    {selectedPostForEditAttachments.map((attachment) => {
                      const selectedForRemoval =
                        formState.removeAttachmentIds.includes(attachment.id);
                      return (
                        <label
                          key={attachment.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedForRemoval}
                            onChange={() => {
                              setFormState((prev) => ({
                                ...prev,
                                removeAttachmentIds: selectedForRemoval
                                  ? prev.removeAttachmentIds.filter(
                                      (id) => id !== attachment.id,
                                    )
                                  : [
                                      ...prev.removeAttachmentIds,
                                      attachment.id,
                                    ],
                              }));
                            }}
                          />
                          <span>
                            Remove {attachment.type}:{" "}
                            {attachment.label || attachment.url}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Link attachments
                </label>
                <div className="space-y-2">
                  {formState.links.map((link, index) => (
                    <div
                      key={`link-${index}`}
                      className="flex items-center gap-2"
                    >
                      <input
                        value={link}
                        onChange={(event) =>
                          updateLink(index, event.target.value)
                        }
                        placeholder="https://..."
                        className="flex-1 rounded-lg border border-[#D0D7F2] px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeLinkField(index)}
                        className="rounded-lg border border-[#F7C5CC] px-3 py-2 text-xs text-[#B42318]"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addLinkField}
                  className="mt-2 rounded-lg border border-[#D0D7F2] px-3 py-2 text-xs text-[#1234CD]"
                >
                  Add link
                </button>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg border border-[#D0D7F2] px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting
                  ? "Saving..."
                  : editingPost
                    ? "Update post"
                    : "Publish post"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {reactionModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {reactionModal.type} reactions
              </h3>
              <button
                type="button"
                onClick={() =>
                  setReactionModal({
                    open: false,
                    type: "Like",
                    people: [],
                    loading: false,
                    error: "",
                  })
                }
                className="text-2xl text-[#667085]"
              >
                &times;
              </button>
            </div>
            {reactionModal.loading ? (
              <p className="text-sm text-gray-500">Loading reactions...</p>
            ) : null}
            {reactionModal.error ? (
              <p className="text-sm text-red-600">{reactionModal.error}</p>
            ) : null}
            {!reactionModal.loading &&
            !reactionModal.error &&
            reactionModal.people.length === 0 ? (
              <p className="text-sm text-gray-500">No reactions yet.</p>
            ) : null}
            <div className="space-y-2">
              {reactionModal.people.map((person, index) => (
                <div
                  key={`${person.email}-${index}`}
                  className="rounded-lg border border-[#EEF1FB] p-3 text-sm"
                >
                  <p className="font-medium text-[#071C50]">
                    {person.name || "-"}
                  </p>
                  <p className="text-[#667085]">{person.email || "-"}</p>
                  <p className="text-xs text-[#98A2B3]">
                    {formatDateTime(person.reactedAt)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {commentModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Comments</h3>
              <button
                type="button"
                onClick={() =>
                  setCommentModal({
                    open: false,
                    post: null,
                    text: "",
                    loading: false,
                    loadingMore: false,
                    error: "",
                    page: 1,
                    hasMore: false,
                    mention: null,
                    editingId: "",
                    editText: "",
                    savingEdit: false,
                  })
                }
                className="text-2xl text-[#667085]"
              >
                &times;
              </button>
            </div>

            <div className="space-y-2">
              {(commentModal.post?.comments || []).map((comment) => (
                <div
                  key={comment.id}
                  className="rounded-lg border border-[#EEF1FB] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-[#071C50]">
                        {comment.commenterName}
                      </p>
                      <p className="text-xs text-[#667085]">
                        {comment.commenterEmail || "-"}
                      </p>
                      <p className="text-xs text-[#98A2B3]">
                        {formatDateTime(comment.createdAt)}
                        {wasCommentEdited(comment) ? (
                          <span className="ml-2 rounded-full bg-[#EEF4FF] px-2 py-0.5 font-medium text-[#1234CD]">Edited</span>
                        ) : null}
                      </p>
                    </div>
                    {comment.canManage ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setCommentModal((prev) => ({
                            ...prev,
                            editingId: comment.id,
                            editText: comment.body,
                            error: "",
                          }))}
                          className="rounded-lg border border-[#D0D7F2] px-2 py-1 text-xs text-[#344054]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => replyToComment(comment)}
                          className="rounded-lg border border-[#D0D7F2] px-2 py-1 text-xs text-[#1234CD]"
                        >
                          Reply
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCommentModal({ open: true, postId: commentModal.post.id, comment, loading: false, error: "" })}
                          className="rounded-lg border border-[#F7C5CC] px-2 py-1 text-xs text-[#B42318]"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => replyToComment(comment)} className="rounded-lg border border-[#D0D7F2] px-2 py-1 text-xs text-[#1234CD]">Reply</button>
                    )}
                  </div>
                  {commentModal.editingId === comment.id ? (
                    <div className="mt-2">
                      <textarea
                        value={commentModal.editText}
                        onChange={(event) => setCommentModal((prev) => ({ ...prev, editText: event.target.value }))}
                        rows={3}
                        className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2 text-sm"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setCommentModal((prev) => ({ ...prev, editingId: "", editText: "" }))} className="rounded-lg border border-[#D0D7F2] px-3 py-1 text-xs">Cancel</button>
                        <button type="button" disabled={commentModal.savingEdit} onClick={() => saveCommentEdit(commentModal.post.id, comment.id)} className="rounded-lg bg-[#1234CD] px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">{commentModal.savingEdit ? "Saving..." : "Save"}</button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-[#344054]">
                      {renderMentions(comment.body)}
                    </p>
                  )}
                  {(comment.replies || []).length ? (
                    <div className="mt-3 space-y-2 border-l-2 border-[#DCE3FF] pl-3 sm:pl-4">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="rounded-lg bg-[#F8FAFF] p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-[#071C50]">{reply.commenterName}</p>
                              <p className="text-xs text-[#667085]">{reply.commenterEmail || "-"}</p>
                              <p className="text-xs text-[#98A2B3]">{formatDateTime(reply.createdAt)}{wasCommentEdited(reply) ? <span className="ml-2 rounded-full bg-[#EEF4FF] px-2 py-0.5 font-medium text-[#1234CD]">Edited</span> : null}</p>
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              <button type="button" onClick={() => replyToComment(comment)} className="rounded-lg border border-[#D0D7F2] px-2 py-1 text-xs text-[#1234CD]">Reply</button>
                              {reply.canManage ? <><button type="button" onClick={() => setCommentModal((prev) => ({ ...prev, editingId: reply.id, editText: reply.body, error: "" }))} className="rounded-lg border border-[#D0D7F2] px-2 py-1 text-xs">Edit</button><button type="button" onClick={() => setDeleteCommentModal({ open: true, postId: commentModal.post.id, comment: reply, loading: false, error: "" })} className="rounded-lg border border-[#F7C5CC] px-2 py-1 text-xs text-[#B42318]">Delete</button></> : null}
                            </div>
                          </div>
                          {commentModal.editingId === reply.id ? <div className="mt-2"><textarea value={commentModal.editText} onChange={(event) => setCommentModal((prev) => ({ ...prev, editText: event.target.value }))} rows={2} className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2 text-sm" /><div className="mt-2 flex justify-end gap-2"><button type="button" onClick={() => setCommentModal((prev) => ({ ...prev, editingId: "", editText: "" }))} className="rounded-lg border px-3 py-1 text-xs">Cancel</button><button type="button" disabled={commentModal.savingEdit} onClick={() => saveCommentEdit(commentModal.post.id, reply.id)} className="rounded-lg bg-[#1234CD] px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">{commentModal.savingEdit ? "Saving..." : "Save"}</button></div></div> : <p className="mt-2 whitespace-pre-wrap text-sm text-[#344054]">{renderMentions(reply.body)}</p>}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {commentModal.hasMore ? (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={loadMoreComments}
                  disabled={commentModal.loadingMore}
                  className="rounded-lg border border-[#D0D7F2] px-3 py-2 text-xs"
                >
                  {commentModal.loadingMore
                    ? "Loading..."
                    : "Load more comments"}
                </button>
              </div>
            ) : null}

            <div className="mt-4 border-t border-[#EEF1FB] pt-3">
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium">{commentModal.replyingTo ? `Reply to ${commentModal.replyingTo.commenterName}` : "Add Comment"}</label>
                {commentModal.replyingTo ? <button type="button" onClick={() => setCommentModal((prev) => ({ ...prev, replyingTo: null, text: "", mention: null }))} className="text-xs font-medium text-[#667085]">Cancel reply</button> : null}
              </div>
              <textarea
                ref={commentComposerRef}
                value={commentModal.text}
                onChange={(event) => updateCommentText(event.target.value)}
                rows={3}
                className="w-full rounded-lg border border-[#D0D7F2] px-3 py-2 text-sm"
              />
              {commentModal.mention && mentionCandidates.length > 0 ? (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-[#E4E7EC] bg-white">
                  {mentionCandidates.map((employee) => (
                    <button
                      key={employee.id}
                      type="button"
                      onClick={() => insertMention(employee)}
                      className="block w-full border-b border-[#F2F4F7] px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[#F8FAFF]"
                    >
                      {employee.name} ({employee.emailAddress || "-"})
                    </button>
                  ))}
                </div>
              ) : null}
              <p className="mt-1 text-xs text-[#98A2B3]">
                Type @ to mention an employee.
              </p>
              {commentModal.error ? (
                <p className="mt-1 text-xs text-red-600">
                  {commentModal.error}
                </p>
              ) : null}
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={commentModal.loading}
                  className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {commentModal.loading ? "Posting..." : commentModal.replyingTo ? "Post reply" : "Post comment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deleteCommentModal.open ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="delete-comment-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-xl font-bold text-red-600">!</div>
            <h3 id="delete-comment-title" className="mt-4 text-lg font-semibold text-[#071C50]">Delete comment?</h3>
            <p className="mt-2 text-sm text-[#667085]">This comment will be permanently removed. This action cannot be undone.</p>
            {deleteCommentModal.comment?.body ? <p className="mt-4 line-clamp-3 rounded-xl bg-[#F8FAFC] p-3 text-sm text-[#344054]">{deleteCommentModal.comment.body}</p> : null}
            {deleteCommentModal.error ? <p className="mt-3 text-sm text-red-600" role="alert">{deleteCommentModal.error}</p> : null}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" disabled={deleteCommentModal.loading} onClick={() => setDeleteCommentModal({ open: false, postId: "", comment: null, loading: false, error: "" })} className="rounded-lg border border-[#D0D5DD] px-4 py-2 text-sm font-semibold text-[#344054] disabled:opacity-60">Cancel</button>
              <button type="button" disabled={deleteCommentModal.loading} onClick={() => deleteComment(deleteCommentModal.postId, deleteCommentModal.comment.id)} className="rounded-lg bg-[#D92D20] px-4 py-2 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-60">{deleteCommentModal.loading ? "Deleting..." : "Delete comment"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {showAccessModal && companyAccount ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-[#071C50]">Engagement posting access</h3>
                <p className="mt-1 text-sm text-[#667085]">
                  Choose which employees can publish posts. Employees cannot pin, schedule, hide, edit, or delete posts.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAccessModal(false)}
                className="text-2xl text-[#667085]"
                aria-label="Close posting access"
              >
                &times;
              </button>
            </div>

            <div className="divide-y divide-[#E4E7EC] rounded-xl border border-[#E4E7EC]">
              {employees.length === 0 ? (
                <p className="p-5 text-sm text-[#667085]">No approved employees found.</p>
              ) : employees.map((employee) => (
                <div key={employee.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-[#071C50]">{employee.name || "Unnamed employee"}</p>
                    <p className="truncate text-sm text-[#667085]">{employee.emailAddress || "No email address"}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!employee.canCreateEngagementPosts}
                    disabled={updatingAccessId === employee.id}
                    onClick={() => updatePostingAccess(employee)}
                    className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60 ${
                      employee.canCreateEngagementPosts
                        ? "bg-[#ECFDF3] text-[#027A48]"
                        : "bg-[#F2F4F7] text-[#475467]"
                    }`}
                  >
                    {updatingAccessId === employee.id
                      ? "Saving..."
                      : employee.canCreateEngagementPosts
                        ? "Access on"
                        : "Access off"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {deletePostModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#071C50]">
                  Delete post?
                </h3>
                <p className="mt-1 text-sm text-[#667085]">
                  This will permanently remove{" "}
                  {deletePostModal.post?.title
                    ? `“${deletePostModal.post.title}”`
                    : "this post"}
                  .
                </p>
              </div>
              <button
                type="button"
                onClick={closeDeletePostModal}
                className="text-2xl leading-none text-[#667085]"
                aria-label="Close delete confirmation"
              >
                &times;
              </button>
            </div>

            {deletePostModal.error ? (
              <p className="mt-3 rounded-lg bg-[#FFF1F3] px-3 py-2 text-sm text-[#B42318]">
                {deletePostModal.error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeletePostModal}
                disabled={deletePostModal.loading}
                className="rounded-lg border border-[#D0D7F2] px-4 py-2 text-sm disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePost}
                disabled={deletePostModal.loading}
                className="rounded-lg bg-[#B42318] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {deletePostModal.loading ? "Deleting..." : "Delete post"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
